
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { DetalleProvisional, DetalleProvisionalFields, detalleProvisional } from '../NuevoSistemaWRMercado';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { ITableColumn, TableList } from 'src/components/table';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AsyncSelect, ControlledTextField } from 'src/components/forms';
import { ICON_MAS } from 'src/pages/sistema/components/SharedIcons';
import { InstrumentoPutFields } from '../api/instrumento-fields';
import CardActions from 'src/pages/sistema/components/CardActions';
import DeleteIcon from '@mui/icons-material/Delete';
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog';
import { HttpCacheOperacion } from 'src/pages/sistema/api/httpCacheOperacion';
import { EntityWithIdAndDescriptionFields, EntityWithIdFields } from 'src/types';
import { v4 as uuid } from 'uuid'

const AsyncLabel = ({ codLimiteTipo }: { codLimiteTipo: number }) => {

    const [label, setLabel] = useState<string>()

    useEffect(() => {
        HttpCacheOperacion.getLimiteTipos()
            .then(limites => {
                const l = limites.find(limite =>
                    limite[EntityWithIdFields.Id] === codLimiteTipo
                )?.[EntityWithIdAndDescriptionFields.Description]

                setLabel(l)
            })
    }, [codLimiteTipo])

    return !label ? <></> : <>{label}</>
}

export const SeccionWRMDetalleNuevo = (
    {
        idPais,
        detallesProvisionales,
        setDetallesProvisionales
    }: {
        idPais: number,
        detallesProvisionales: DetalleProvisional[],
        setDetallesProvisionales: Dispatch<SetStateAction<DetalleProvisional[]>>
    }) => {

    const [openDialog, setOpenDialog] = useState<boolean>(false);

    const { askConfirmation } = useConfirmationDialog();

    const columnsTablaWRMInstrumento: ITableColumn[] = [
        { label: 'Excepción', value: DetalleProvisionalFields.Excepcion },
        { label: 'Instrumento', value: DetalleProvisionalFields.Instrumento },
        {
            label: 'Límite',
            onRenderCell: (entity: DetalleProvisional) => (
                <AsyncLabel codLimiteTipo={entity[DetalleProvisionalFields.CodOperacionLimiteTipo]} />
            )
        },
        { label: 'Valor nominal', value: DetalleProvisionalFields.ValorNominal, type: 5 },
        { label: 'Cash value USD', value: DetalleProvisionalFields.CashValueUSD, type: 5 },
        {
            label: 'Acciones', onRenderCell: (entity: DetalleProvisional) => (
                <Tooltip title='Eliminar'>
                    <IconButton
                        onClick={
                            () => askConfirmation(
                                () => setDetallesProvisionales(
                                    dts => dts.filter(dt => dt.idProvisional !== entity.idProvisional)
                                )
                            )
                        }
                    >
                        <DeleteIcon color='error' />
                    </IconButton>
                </Tooltip>
            ),
            width: 0.1
        }
    ]

    const { control, handleSubmit, reset } = useForm<DetalleProvisional>({
        resolver: zodResolver(detalleProvisional),
        defaultValues: {
            [DetalleProvisionalFields.IdProvisional]: uuid(),
            [DetalleProvisionalFields.Excepcion]: "Minuta"
        }
    });

    const alGuardarDetalleProvisional = (data: DetalleProvisional) => {
        const nuevoDetalle: DetalleProvisional = {
            ...data,
            idProvisional: uuid(),
        }
        setDetallesProvisionales(prev => [...prev, nuevoDetalle])
        reset()
        setOpenDialog(false)
    }

    return (
        <CardActions
            title={"Operación MinutaMercado Instrumento"}
            actions={
                <Button variant="contained" color="primary" endIcon={ICON_MAS} onClick={() => setOpenDialog(true)}>
                    Nuevo
                </Button>
            }
            colapsable
            tableContent
        >
            <TableList
                entityList={detallesProvisionales}
                columns={columnsTablaWRMInstrumento}
                isLoading={false}
                error={false}
            />
            {
                openDialog &&
                <Dialog
                    open={openDialog}
                    maxWidth='sm'
                    fullWidth
                >
                    <DialogTitle>Nuevo instrumento</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={4} pt={2}>
                            <Grid item xs={12} md={6}>
                                <ControlledTextField
                                    control={control}
                                    name={InstrumentoPutFields.ValorNominal}
                                    label='Valor nominal'
                                    numeric
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ControlledTextField
                                    control={control}
                                    name={InstrumentoPutFields.CashValueUSD}
                                    label='Cash value USD'
                                    numeric
                                    currency
                                    prefix={'USD'}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <AsyncSelect
                                    control={control}
                                    name={InstrumentoPutFields.CodOperacionLimiteTipo}
                                    label='Límite'
                                    fullWidth
                                    loadOptions={() => HttpCacheOperacion.getLimiteTipos()}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ControlledTextField
                                    control={control}
                                    name={InstrumentoPutFields.Instrumento}
                                    label='Instrumento'
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <ControlledTextField
                                    control={control}
                                    name={InstrumentoPutFields.Excepcion}
                                    label='Excepción'
                                    multiline
                                    rows={2}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button variant='text' color='primary' onClick={() => setOpenDialog(false)}>
                            Cancelar
                        </Button>
                        <Button variant='contained' color='primary' onClick={handleSubmit(alGuardarDetalleProvisional)}>
                            Agregar
                        </Button>
                    </DialogActions>
                </Dialog>
            }
        </CardActions>
    )
}
